import { FC, useState, useMemo } from 'react'
import {
    Button,
    Tooltip,
    App,
    Table,
    Input,
    Select,
    Space,
    Card,
    Statistic,
    Row,
    Col,
    Modal,
    Tag,
    Radio,
    Typography,
} from 'antd'
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons'
import { useVerifyCalculationsMutation } from '../../../../store/api/calculation-positions-api'
import TableWrapper from '../../../../components/table-wrapper/table-wrapper'
import type { ColumnsType } from 'antd/es/table'
import styled from 'styled-components'

const { Text } = Typography

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    max-width: 1400px;
    min-height: 400px;
    flex: 1 1 auto;
`

const CardStyled = styled(Card)`
    .ant-statistic-title {
        font-size: 12px;
    }
`

const ComparisonCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 4px 0;
`

const ExcelValue = styled.div<{ $isDifferent: boolean }>`
    color: ${props => (props.$isDifferent ? '#ff4d4f' : '#52c41a')};
    font-weight: ${props => (props.$isDifferent ? 'bold' : 'normal')};
    background: ${props => (props.$isDifferent ? '#fff1f0' : 'transparent')};
    padding: 2px 4px;
    border-radius: 2px;
`

const SystemValue = styled.div<{ $isDifferent: boolean }>`
    color: ${props => (props.$isDifferent ? '#ff4d4f' : '#52c41a')};
    font-weight: ${props => (props.$isDifferent ? 'bold' : 'normal')};
    background: ${props => (props.$isDifferent ? '#fff1f0' : 'transparent')};
    padding: 2px 4px;
    border-radius: 2px;
`

const GroupedRow = styled.div`
    margin-bottom: 16px;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    padding: 12px;
    background: #fafafa;
`

const GroupedRowHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e8e8e8;
`

const GroupedRowContent = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 12px;
`

interface VerificationDetail {
    excelId: string
    rowNumber: number
    field: string
    excelValue: any
    systemValue: any
}

interface VerifyCalculationsProps {}

type ViewMode = 'table' | 'grouped'

const VerifyCalculations: FC<VerifyCalculationsProps> = () => {
    const { notification } = App.useApp()
    const [verifyCalculations, { isLoading }] = useVerifyCalculationsMutation()
    const [details, setDetails] = useState<VerificationDetail[]>([])
    const [fieldFilter, setFieldFilter] = useState<string | undefined>()
    const [excelIdFilter, setExcelIdFilter] = useState<string>('')
    const [rowNumberFilter, setRowNumberFilter] = useState<number | undefined>()
    const [viewMode, setViewMode] = useState<ViewMode>('grouped')
    const [selectedRow, setSelectedRow] = useState<number | null>(null)
    const [modalVisible, setModalVisible] = useState(false)

    const handleVerify = async () => {
        try {
            const result = await verifyCalculations().unwrap()
            setDetails(result.details || [])

            if (result.details?.length === 0) {
                notification.success({
                    message: 'Проверка завершена',
                    description: 'Все значения совпадают! Несовпадений не найдено.',
                    placement: 'bottomRight',
                    duration: 5,
                })
            } else {
                notification.warning({
                    message: 'Проверка завершена',
                    description: `Найдено несовпадений: ${result.details?.length || 0}`,
                    placement: 'bottomRight',
                    duration: 5,
                })
            }
        } catch (err: any) {
            const errorMessage =
                err?.data?.error?.message || err?.message || 'Произошла ошибка при проверке'

            notification.error({
                message: 'Ошибка при проверке',
                description: errorMessage,
                placement: 'bottomRight',
                duration: 5,
            })
            console.error('Ошибка при проверке:', err)
            setDetails([])
        }
    }

    const filteredDetails = useMemo(() => {
        return details.filter(detail => {
            if (fieldFilter && detail.field !== fieldFilter) return false
            if (
                excelIdFilter &&
                !detail.excelId.toLowerCase().includes(excelIdFilter.toLowerCase())
            )
                return false
            if (rowNumberFilter !== undefined && detail.rowNumber !== rowNumberFilter) return false
            return true
        })
    }, [details, fieldFilter, excelIdFilter, rowNumberFilter])

    const uniqueFields = useMemo(() => {
        return Array.from(new Set(details.map(d => d.field))).sort()
    }, [details])

    const statistics = useMemo(() => {
        const total = details.length
        const uniqueRows = new Set(details.map(d => d.rowNumber)).size
        return { total, uniqueRows }
    }, [details])

    const groupedByRow = useMemo(() => {
        const grouped = new Map<number, VerificationDetail[]>()
        filteredDetails.forEach(detail => {
            if (!grouped.has(detail.rowNumber)) {
                grouped.set(detail.rowNumber, [])
            }
            grouped.get(detail.rowNumber)!.push(detail)
        })
        return Array.from(grouped.entries())
            .map(([rowNumber, items]) => ({
                rowNumber,
                excelId: items[0].excelId,
                details: items,
            }))
            .sort((a, b) => a.rowNumber - b.rowNumber)
    }, [filteredDetails])

    const selectedRowDetails = useMemo(() => {
        if (selectedRow === null) return []
        return details.filter(d => d.rowNumber === selectedRow)
    }, [details, selectedRow])

    const handleExportCSV = () => {
        const headers = [
            'Номер строки',
            'Excel ID',
            'Поле',
            'Значение в Excel',
            'Значение в системе',
            'Разница',
        ]
        const rows = filteredDetails.map(detail => {
            const excelValue = detail.excelValue
            const systemValue = detail.systemValue
            let diff = ''
            if (typeof excelValue === 'number' && typeof systemValue === 'number') {
                diff = (excelValue - systemValue).toString()
            }
            return [
                detail.rowNumber,
                detail.excelId,
                detail.field,
                excelValue?.toString() || '',
                systemValue?.toString() || '',
                diff,
            ]
        })
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
        ].join('\n')
        const blob = new Blob(['\ufeff' + csvContent], {
            type: 'text/csv;charset=utf-8;',
        })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `verification-${new Date().toISOString().split('T')[0]}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    const formatValue = (value: any): string => {
        if (value === null || value === undefined) return '—'
        if (typeof value === 'number') {
            return value.toLocaleString('ru-RU', { maximumFractionDigits: 6 })
        }
        return String(value)
    }

    const calculateDifference = (excelValue: any, systemValue: any) => {
        if (typeof excelValue === 'number' && typeof systemValue === 'number') {
            const diff = excelValue - systemValue
            return { diff, isDifferent: Math.abs(diff) >= 0.01 }
        }
        return { diff: null, isDifferent: excelValue !== systemValue }
    }

    const columns: ColumnsType<VerificationDetail> = [
        {
            title: 'Номер строки',
            dataIndex: 'rowNumber',
            key: 'rowNumber',
            width: 100,
            sorter: (a, b) => a.rowNumber - b.rowNumber,
            defaultSortOrder: 'ascend',
            render: (value: number) => (
                <Button
                    type='link'
                    size='small'
                    onClick={() => {
                        setSelectedRow(value)
                        setModalVisible(true)
                    }}
                    icon={<EyeOutlined />}
                >
                    {value}
                </Button>
            ),
        },
        {
            title: 'Excel ID',
            dataIndex: 'excelId',
            key: 'excelId',
            width: 150,
            ellipsis: true,
        },
        {
            title: 'Поле',
            dataIndex: 'field',
            key: 'field',
            width: 150,
            filters: uniqueFields.map(field => ({ text: field, value: field })),
            onFilter: (value, record) => record.field === value,
        },
        {
            title: 'Сравнение',
            key: 'comparison',
            width: 250,
            render: (_, record) => {
                const { excelValue, systemValue } = record
                const { isDifferent } = calculateDifference(excelValue, systemValue)
                return (
                    <ComparisonCell>
                        <div>
                            <Text type='secondary' style={{ fontSize: '11px' }}>
                                Excel:
                            </Text>
                            <ExcelValue $isDifferent={isDifferent}>
                                {formatValue(excelValue)}
                            </ExcelValue>
                        </div>
                        <div>
                            <Text type='secondary' style={{ fontSize: '11px' }}>
                                Система:
                            </Text>
                            <SystemValue $isDifferent={isDifferent}>
                                {formatValue(systemValue)}
                            </SystemValue>
                        </div>
                    </ComparisonCell>
                )
            },
        },
        {
            title: 'Разница',
            key: 'difference',
            width: 120,
            render: (_, record) => {
                const { excelValue, systemValue } = record
                const { diff, isDifferent } = calculateDifference(excelValue, systemValue)
                if (diff !== null) {
                    return (
                        <div
                            style={{
                                color: isDifferent ? '#ff4d4f' : '#52c41a',
                                fontWeight: 'bold',
                            }}
                        >
                            {diff > 0 ? '+' : ''}
                            {formatValue(diff)}
                        </div>
                    )
                }
                return <span style={{ color: '#999' }}>—</span>
            },
        },
    ]

    return (
        <Container>
            <Tooltip title='Проверяет соответствие данных из Excel с данными в системе и показывает несовпадения'>
                <Button onClick={handleVerify} loading={isLoading} type='primary'>
                    Проверить соответствие с Excel
                </Button>
            </Tooltip>

            {details.length > 0 && (
                <>
                    <CardStyled>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Statistic
                                    title='Всего несовпадений'
                                    value={statistics.total}
                                    valueStyle={{ color: '#ff4d4f' }}
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title='Уникальных строк Excel'
                                    value={statistics.uniqueRows}
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic title='Показывается' value={filteredDetails.length} />
                            </Col>
                            <Col span={6}>
                                <Statistic title='Уникальных полей' value={uniqueFields.length} />
                            </Col>
                        </Row>
                    </CardStyled>

                    <Card
                        title='Настройки отображения'
                        size='small'
                        extra={
                            <Space>
                                <Radio.Group
                                    value={viewMode}
                                    onChange={e => setViewMode(e.target.value)}
                                    size='small'
                                >
                                    <Radio.Button value='grouped'>Группировка</Radio.Button>
                                    <Radio.Button value='table'>Таблица</Radio.Button>
                                </Radio.Group>
                                <Button
                                    icon={<DownloadOutlined />}
                                    onClick={handleExportCSV}
                                    size='small'
                                >
                                    Экспорт CSV
                                </Button>
                            </Space>
                        }
                    />

                    <Card
                        title='Фильтры'
                        size='small'
                        extra={
                            <Button
                                size='small'
                                onClick={() => {
                                    setFieldFilter(undefined)
                                    setExcelIdFilter('')
                                    setRowNumberFilter(undefined)
                                }}
                            >
                                Сбросить
                            </Button>
                        }
                    >
                        <Space direction='vertical' style={{ width: '100%' }} size='middle'>
                            <Space wrap>
                                <span>Поле:</span>
                                <Select
                                    style={{ width: 250 }}
                                    placeholder='Выберите поле'
                                    allowClear
                                    value={fieldFilter}
                                    onChange={setFieldFilter}
                                    options={uniqueFields.map(field => ({
                                        label: field,
                                        value: field,
                                    }))}
                                />
                                <span>Excel ID:</span>
                                <Input
                                    style={{ width: 200 }}
                                    placeholder='Поиск по Excel ID'
                                    value={excelIdFilter}
                                    onChange={e => setExcelIdFilter(e.target.value)}
                                    allowClear
                                />
                                <span>Номер строки:</span>
                                <Input
                                    style={{ width: 150 }}
                                    type='number'
                                    placeholder='Номер строки'
                                    value={rowNumberFilter ?? ''}
                                    onChange={e => {
                                        const val = e.target.value
                                        setRowNumberFilter(val ? parseInt(val, 10) : undefined)
                                    }}
                                    allowClear
                                />
                            </Space>
                        </Space>
                    </Card>

                    {viewMode === 'grouped' ? (
                        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                            {groupedByRow.map(({ rowNumber, excelId, details: rowDetails }) => (
                                <GroupedRow key={rowNumber}>
                                    <GroupedRowHeader>
                                        <div>
                                            <Text strong>Строка {rowNumber}</Text>
                                            <Tag style={{ marginLeft: 8 }}>{excelId}</Tag>
                                            <Tag color='error'>
                                                {rowDetails.length} несовпадений
                                            </Tag>
                                        </div>
                                        <Button
                                            type='link'
                                            size='small'
                                            icon={<EyeOutlined />}
                                            onClick={() => {
                                                setSelectedRow(rowNumber)
                                                setModalVisible(true)
                                            }}
                                        >
                                            Детали
                                        </Button>
                                    </GroupedRowHeader>
                                    <GroupedRowContent>
                                        {rowDetails.map((detail, idx) => {
                                            const { diff, isDifferent } = calculateDifference(
                                                detail.excelValue,
                                                detail.systemValue
                                            )
                                            return (
                                                <Card
                                                    key={idx}
                                                    size='small'
                                                    style={{
                                                        borderColor: isDifferent
                                                            ? '#ff4d4f'
                                                            : '#d9d9d9',
                                                    }}
                                                >
                                                    <div style={{ marginBottom: 8 }}>
                                                        <Tag
                                                            color={
                                                                isDifferent ? 'error' : 'success'
                                                            }
                                                        >
                                                            {detail.field}
                                                        </Tag>
                                                    </div>
                                                    <div style={{ marginBottom: 4 }}>
                                                        <Text
                                                            type='secondary'
                                                            style={{ fontSize: '11px' }}
                                                        >
                                                            Excel:{' '}
                                                        </Text>
                                                        <ExcelValue $isDifferent={isDifferent}>
                                                            {formatValue(detail.excelValue)}
                                                        </ExcelValue>
                                                    </div>
                                                    <div style={{ marginBottom: 4 }}>
                                                        <Text
                                                            type='secondary'
                                                            style={{ fontSize: '11px' }}
                                                        >
                                                            Система:{' '}
                                                        </Text>
                                                        <SystemValue $isDifferent={isDifferent}>
                                                            {formatValue(detail.systemValue)}
                                                        </SystemValue>
                                                    </div>
                                                    {diff !== null && (
                                                        <div>
                                                            <Text
                                                                strong
                                                                style={{
                                                                    color: isDifferent
                                                                        ? '#ff4d4f'
                                                                        : '#52c41a',
                                                                }}
                                                            >
                                                                {diff > 0 ? '+' : ''}
                                                                {formatValue(diff)}
                                                            </Text>
                                                        </div>
                                                    )}
                                                </Card>
                                            )
                                        })}
                                    </GroupedRowContent>
                                </GroupedRow>
                            ))}
                        </div>
                    ) : (
                        <TableWrapper
                            style={{ flex: '1 1 auto', minHeight: 300 }}
                            filtersSummaryHeight={0}
                        >
                            <Table
                                bordered
                                columns={columns}
                                dataSource={filteredDetails}
                                rowKey={record => `${record.excelId}-${record.field}`}
                                pagination={{
                                    pageSize: 50,
                                    showSizeChanger: true,
                                    showTotal: total => `Всего: ${total}`,
                                }}
                                size='small'
                                scroll={{ x: 'max-content' }}
                            />
                        </TableWrapper>
                    )}

                    <Modal
                        title={`Детали строки ${selectedRow}`}
                        open={modalVisible}
                        onCancel={() => setModalVisible(false)}
                        footer={null}
                        width={800}
                    >
                        {selectedRowDetails.length > 0 && (
                            <Table
                                columns={[
                                    {
                                        title: 'Поле',
                                        dataIndex: 'field',
                                        key: 'field',
                                        width: 150,
                                    },
                                    {
                                        title: 'Значение в Excel',
                                        key: 'excelValue',
                                        width: 200,
                                        render: (_, record) => {
                                            const { isDifferent } = calculateDifference(
                                                record.excelValue,
                                                record.systemValue
                                            )
                                            return (
                                                <ExcelValue $isDifferent={isDifferent}>
                                                    {formatValue(record.excelValue)}
                                                </ExcelValue>
                                            )
                                        },
                                    },
                                    {
                                        title: 'Значение в системе',
                                        key: 'systemValue',
                                        width: 200,
                                        render: (_, record) => {
                                            const { isDifferent } = calculateDifference(
                                                record.excelValue,
                                                record.systemValue
                                            )
                                            return (
                                                <SystemValue $isDifferent={isDifferent}>
                                                    {formatValue(record.systemValue)}
                                                </SystemValue>
                                            )
                                        },
                                    },
                                    {
                                        title: 'Разница',
                                        key: 'difference',
                                        width: 150,
                                        render: (_, record) => {
                                            const { diff } = calculateDifference(
                                                record.excelValue,
                                                record.systemValue
                                            )
                                            if (diff !== null) {
                                                return (
                                                    <div
                                                        style={{
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        {diff > 0 ? '+' : ''}
                                                        {formatValue(diff)}
                                                    </div>
                                                )
                                            }
                                            return '—'
                                        },
                                    },
                                ]}
                                dataSource={selectedRowDetails}
                                rowKey={record => record.field}
                                pagination={false}
                                size='small'
                            />
                        )}
                    </Modal>
                </>
            )}
        </Container>
    )
}

export default VerifyCalculations
